import {
  ZadAuditUser,
  ZadInitialStockPayload,
  ZadProduct,
  ZadStockAdjustmentDirection,
  ZadStockAdjustmentPayload,
  ZadStockMovement,
  ZadStockMovementType,
  ZadStockOverviewItem,
} from '../../../models/zad'
import {ZadApiError, resolveZadUserMessage} from './errors'

export const ZAD_MANUAL_MOVEMENT_TYPES: ZadStockMovementType[] = [
  'INITIAL_STOCK',
  'ADJUSTMENT_IN',
  'ADJUSTMENT_OUT',
]

export const ZAD_RESERVED_MOVEMENT_TYPES: ZadStockMovementType[] = [
  'PURCHASE',
  'SALE',
  'CUSTOMER_RETURN',
  'SUPPLIER_RETURN',
]

export const ZAD_MOVEMENT_TYPE_I18N: Record<ZadStockMovementType, string> = {
  INITIAL_STOCK: 'ZAD.STOCK.TYPE.INITIAL_STOCK',
  ADJUSTMENT_IN: 'ZAD.STOCK.TYPE.ADJUSTMENT_IN',
  ADJUSTMENT_OUT: 'ZAD.STOCK.TYPE.ADJUSTMENT_OUT',
  PURCHASE: 'ZAD.STOCK.TYPE.PURCHASE',
  SALE: 'ZAD.STOCK.TYPE.SALE',
  CUSTOMER_RETURN: 'ZAD.STOCK.TYPE.CUSTOMER_RETURN',
  SUPPLIER_RETURN: 'ZAD.STOCK.TYPE.SUPPLIER_RETURN',
}

export function getZadStockStatus(currentStock: number): 'in_stock' | 'out_of_stock' {
  return currentStock > 0 ? 'in_stock' : 'out_of_stock'
}

export function getZadStockStatusI18n(currentStock: number): string {
  return currentStock > 0 ? 'ZAD.STOCK.STATUS_IN' : 'ZAD.STOCK.STATUS_OUT'
}

export function formatZadSignedQuantity(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  if (value > 0) return `+${value}`
  return String(value)
}

export function formatZadDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatZadUser(user: ZadAuditUser | string | null | undefined): string {
  if (!user) return '—'
  if (typeof user === 'string') return '—'
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim()
  return name || user.email || '—'
}

export function formatZadMovementReference(movement: Pick<
  ZadStockMovement,
  'referenceType' | 'referenceId'
>): string {
  const type = movement.referenceType ? String(movement.referenceType).trim() : ''
  const id = movement.referenceId ? String(movement.referenceId).trim() : ''
  if (type && id) return `${type} ${id}`
  if (type) return type
  if (id) return id
  return '—'
}

export function getZadMovementProduct(
  product: ZadStockMovement['product'] | ZadProduct | null | undefined
): ZadProduct | null {
  if (!product || typeof product === 'string') return null
  return product
}

export function getZadMovementProductId(
  product: ZadStockMovement['product'] | ZadProduct | string | null | undefined
): string {
  if (!product) return ''
  if (typeof product === 'string') return product
  return product._id || ''
}

export function getZadMovementProductName(
  product: ZadStockMovement['product'] | ZadProduct | string | null | undefined
): string {
  const populated = getZadMovementProduct(product)
  return populated?.name || '—'
}

export function parseZadStockQuantity(
  value: string | number | null | undefined
): {ok: true; value: number} | {ok: false; errorId: string} {
  if (value === undefined || value === null || value === '') {
    return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_REQUIRED'}
  }
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value <= 0) {
      if (value === 0) return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_ZERO'}
      if (value < 0) return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_NEGATIVE'}
      return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_INTEGER'}
    }
    return {ok: true, value}
  }
  const normalized = String(value).trim()
  if (!normalized) return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_REQUIRED'}
  if (normalized.startsWith('-')) return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_NEGATIVE'}
  if (!/^\d+$/.test(normalized)) return {ok: false, errorId: 'ZAD.STOCK.QUANTITY_INTEGER'}
  const numeric = Number(normalized)
  if (!Number.isInteger(numeric) || numeric <= 0) {
    return {ok: false, errorId: numeric === 0 ? 'ZAD.STOCK.QUANTITY_ZERO' : 'ZAD.STOCK.QUANTITY_INTEGER'}
  }
  return {ok: true, value: numeric}
}

export function predictZadStockAfterAdjustment(
  currentStock: number,
  direction: ZadStockAdjustmentDirection,
  quantity: number
): number {
  return direction === 'IN' ? currentStock + quantity : currentStock - quantity
}

export function canZadAdjustOut(currentStock: number, quantity: number): boolean {
  return quantity > 0 && quantity <= currentStock
}

export function validateZadInitialStockForm(values: {
  product: string
  quantity: string
  reason: string
}): string | null {
  if (!String(values.product || '').trim()) return 'ZAD.STOCK.PRODUCT_REQUIRED'
  const quantity = parseZadStockQuantity(values.quantity)
  if (!quantity.ok) return quantity.errorId
  if (!String(values.reason || '').trim()) return 'ZAD.STOCK.REASON_REQUIRED'
  return null
}

export function validateZadAdjustmentForm(
  values: {
    product: string
    direction: ZadStockAdjustmentDirection
    quantity: string
    reason: string
  },
  currentStock: number
): string | null {
  if (!String(values.product || '').trim()) return 'ZAD.STOCK.PRODUCT_REQUIRED'
  if (values.direction !== 'IN' && values.direction !== 'OUT') {
    return 'ZAD.STOCK.DIRECTION_REQUIRED'
  }
  const quantity = parseZadStockQuantity(values.quantity)
  if (!quantity.ok) return quantity.errorId
  if (!String(values.reason || '').trim()) return 'ZAD.STOCK.REASON_REQUIRED'
  if (values.direction === 'OUT' && !canZadAdjustOut(currentStock, quantity.value)) {
    return 'ZAD.STOCK.OUT_EXCEEDS'
  }
  return null
}

export function toZadInitialStockPayload(values: {
  product: string
  quantity: string | number
  reason: string
}): ZadInitialStockPayload {
  const quantity = parseZadStockQuantity(values.quantity)
  return {
    product: String(values.product || '').trim(),
    quantity: quantity.ok ? quantity.value : 0,
    reason: String(values.reason || '').trim(),
  }
}

export function toZadAdjustmentPayload(values: {
  product: string
  direction: ZadStockAdjustmentDirection
  quantity: string | number
  reason: string
}): ZadStockAdjustmentPayload {
  const quantity = parseZadStockQuantity(values.quantity)
  return {
    product: String(values.product || '').trim(),
    direction: values.direction,
    quantity: quantity.ok ? quantity.value : 0,
    reason: String(values.reason || '').trim(),
  }
}

export function getOverviewProductId(item: ZadStockOverviewItem | null | undefined): string {
  return item?.product?._id || ''
}

export function resolveZadStockMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  if (err.errorCode === 'INITIAL_STOCK_ALREADY_EXISTS') {
    return {
      i18nId: 'ZAD.STOCK.INITIAL_EXISTS',
      fallback: 'Initial stock for this product has already been set. Use an adjustment to correct the quantity.',
    }
  }
  return resolveZadUserMessage(err)
}
